import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";

import { Button } from "../Button";
import { CardFooter } from "../Card/Card";
import { Modal } from "../Modal";
import { CONTACT_TYPES_TEXT, TEST_ID_PREFIX } from "./constants";
import { UpdateWarningModal } from "./UpdateWarningModal";
import { ROLE } from "./shared";
import type { Contact, ContactCardProps, ContactType } from "./types";
import { AddNewContact } from "./AddNewContact";
import { ContactInfoModal } from "./ContactInfoModal";
import { ContactOption } from "./ContactOption";

const ContactListEdit = ({
  contactType,
  contactList,
  selectedContactId,
  selectedTempContactId,
  disabled,
  hasOffer,
  disableSameSignerValidation,
  customApprovedStatusText,
  customNotApprovedStatusText,
  customNotCheckedStatusText,
  onContactInfoSave,
  onSelectionUpdate,
  onCancel,
  disableEditEmail,
  secondaryButtonText,
}: ContactListEditProps) => {
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showSameSignerWarning, setShowSameSignerWarning] = useState(false);
  const [editingContact, setEditingContact] = useState(undefined);
  const [tempSelectedContactId, setTempSelectedContactId] =
    useState(selectedContactId);

  const existingPrimarySignerId = useMemo(
    () => contactList?.find((contact) => !!contact.primary)?.contactId,
    [contactList]
  );
  const existingSecondarySignerId = useMemo(
    () =>
      contactList?.find((contact) =>
        contact.derivedRoles.includes(ROLE.CONTRACT_COSIGNER)
      )?.contactId,
    [contactList]
  );

  useEffect(() => {
    if (!selectedTempContactId) {
      return;
    }
    setTempSelectedContactId(selectedTempContactId);
  }, [selectedTempContactId]);

  useEffect(() => {
    if (contactType !== "secondary") {
      return;
    }

    if (selectedContactId || selectedTempContactId) {
      return;
    }

    const primary = contactList?.find((contact) => contact.primary);
    setTempSelectedContactId(primary?.contactId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // This effect should only run once when the component is mounted to setup Secondary Contact properly

  const isSelectedContact = (contactOption: Contact) =>
    contactOption.contactId === tempSelectedContactId;

  const openModal = (contact?: Contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const closeModal = () => {
    setEditingContact(undefined);
    setShowModal(false);
  };

  const handleSelected = (selectedContact: Contact) => {
    setTempSelectedContactId(selectedContact.contactId);
  };

  const handleSave = async (updatedContact: Contact) => {
    const newContact = await onContactInfoSave(updatedContact);

    if (newContact) {
      setTempSelectedContactId(newContact.contactId);
    }
  };

  const isSelectionValid = useMemo(() => {
    if (selectedContactId === tempSelectedContactId) {
      return false;
    }

    return !!contactList.find(
      (contact) => contact.contactId === tempSelectedContactId
    );
  }, [contactList, selectedContactId, tempSelectedContactId]);

  const shouldValidateSameSigner = (contactType: ContactType) => {
    if (disableSameSignerValidation) {
      return false;
    }

    if (contactType === "utility_bill") {
      return false;
    }

    return [existingPrimarySignerId, existingSecondarySignerId].includes(
      tempSelectedContactId
    );
  };

  const getContactById = (contactId: string) =>
    contactList.find((contact) => contact.contactId === contactId);

  const handleSelectionUpdate = () => {
    if (hasOffer) {
      return setShowConfirmationModal(true);
    }
    onSelectionUpdate(getContactById(tempSelectedContactId));
  };

  return (
    <>
      <ContactListContainer>
        {contactList?.map((contact) => (
          <ContactOption
            key={contact.contactId}
            contactType={contactType}
            contact={contact}
            isSelected={isSelectedContact(contact)}
            onSelect={handleSelected}
            onEdit={openModal}
            customApprovedStatusText={customApprovedStatusText}
            customNotApprovedStatusText={customNotApprovedStatusText}
            customNotCheckedStatusText={customNotCheckedStatusText}
          />
        ))}
        <AddNewContact contactType={contactType} onAdd={openModal} />
      </ContactListContainer>
      <ContactInfoModal
        isOpen={showModal}
        contactType={contactType}
        contact={editingContact}
        loading={disabled}
        onSave={handleSave}
        onClose={closeModal}
        disableEditEmail={disableEditEmail}
      />
      <CardFooter
        rightContent={
          <>
            <Button
              size="sm"
              color="secondary"
              onClick={onCancel}
              disabled={disabled}
              data-testid={`${TEST_ID_PREFIX[contactType]}-cancel`}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              disabled={!isSelectionValid || disabled}
              onClick={() => {
                if (shouldValidateSameSigner(contactType)) {
                  return setShowSameSignerWarning(true);
                }

                handleSelectionUpdate();
              }}
              data-testid={`${TEST_ID_PREFIX[contactType]}-update`}
            >
              {secondaryButtonText ??
                `Update ${CONTACT_TYPES_TEXT[contactType]}`}
            </Button>
          </>
        }
      />
      {showConfirmationModal && (
        <UpdateWarningModal
          contactType={contactType}
          onClose={() => {
            setShowConfirmationModal(false);
          }}
          onConfirm={() => {
            setShowConfirmationModal(false);
            onSelectionUpdate(getContactById(tempSelectedContactId));
          }}
        />
      )}

      {showSameSignerWarning && (
        <Modal
          type="warning"
          title="Are you sure you want to select the same person as the Primary Signer and Secondary Signer?"
          primaryButton={{
            text: `Update ${CONTACT_TYPES_TEXT[contactType]}`,
            onClick: () => {
              setShowSameSignerWarning(false);
              handleSelectionUpdate();
            },
          }}
          onClose={() => {
            setShowSameSignerWarning(false);
          }}
          secondaryButton={{
            text: "Go Back",
            onClick: () => {
              setShowSameSignerWarning(false);
            },
          }}
        />
      )}
    </>
  );
};

interface ContactListEditProps extends ContactCardProps {
  selectedContactId: string;
  selectedTempContactId?: string;
  onCancel: () => void;
  disableEditEmail?: boolean;
  secondaryButtonText?: string;
}

const ContactListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export { ContactListEdit };
